import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, Inbox } from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { MetricSkeleton } from '@/components/dashboard/MetricSkeleton';
import { StatCard } from '@/components/dashboard/StatCard';
import { RiskBar } from '@/components/dashboard/RiskBar';
import { SeverityChip } from '@/components/dashboard/SeverityChip';
import { WidgetCardHeader } from '@/components/dashboard/WidgetCardHeader';
import DSRDetailPanel from '@/components/dashboard/DSRDetailPanel';
import { useDsrPanel } from '@/components/dashboard/useDsrPanel';
import { TIME_PERIOD_30D, type TimePeriod } from '@/components/dashboard/timePeriod';
import { useAuthStore } from '@/store/authStore';
import { supabase } from '@/services/db/supabase';
import {
  getTenantOperationsSnapshot,
  getPortfolioRiskDistribution,
  getSafetyEventsAnalysis,
  getHighRiskActivitySummary,
  getPermitComplianceSummary,
  getProjectBreakdown,
  getRecentSafetyEvents,
  getRecentGoodCatch,
  type RiskDistribution,
  type SafetyEventsAnalysis,
  type HighRiskActivitySummary,
  type PermitComplianceSummary,
  type ProjectBreakdownRow,
} from '@/services/db/dashboard';
import {
  getDSRTrend,
  getSafetyEventTrend as getSafetyEventDailyTrend,
  getSSETrend,
  getTopActionItems,
  getProjectIdsForTenant,
} from '@/services/db/analytics';
import { getSSEByProject, type SSEByProjectRow } from '@/services/db/pob';
import { getActiveAlertsBreakdown, type ActiveAlertsBreakdown } from '@/services/db/alerts';
import type { AlertSeverity } from '@/types/alerts';
import {
  ChartCard,
  DailyRiskChart,
  SafetyEventLineChart,
  SSEAreaChart,
  TopActionItemsCard,
  useChartState,
} from '@/components/analytics/AnalyticsCharts';

const STALE = 5 * 60 * 1000;

// ── Hooks ───────────────────────────────────────────────────

function useTenantName() {
  const tenantId = useAuthStore((s) => s.tenantId);
  return useQuery({
    queryKey: ['tenant_name', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('name')
        .eq('id', tenantId!)
        .single();
      if (error) throw error;
      return data?.name ?? 'Your Organization';
    },
    enabled: !!tenantId,
    staleTime: STALE,
  });
}

function useScopeProjectIds() {
  const tenantId = useAuthStore((s) => s.tenantId);
  return useQuery({
    queryKey: ['sm_scope_project_ids', tenantId],
    queryFn: () => getProjectIdsForTenant(tenantId!),
    enabled: !!tenantId,
    staleTime: STALE,
  });
}

function useOperationsSnapshot() {
  const tenantId = useAuthStore((s) => s.tenantId);
  return useQuery({
    queryKey: ['sm_ops_snapshot', tenantId],
    queryFn: () => getTenantOperationsSnapshot(tenantId!),
    enabled: !!tenantId,
    staleTime: STALE,
  });
}

function usePortfolioRisk(projectIds: string[], enabled: boolean, period: TimePeriod) {
  return useQuery({
    queryKey: ['sm_portfolio_risk', projectIds, period.days],
    queryFn: () => getPortfolioRiskDistribution(projectIds, period.days),
    enabled,
    staleTime: STALE,
  });
}

function useSafetyEvents(projectIds: string[], enabled: boolean, period: TimePeriod) {
  return useQuery({
    queryKey: ['sm_safety_events', projectIds, period.days],
    queryFn: () => getSafetyEventsAnalysis(projectIds, period.days),
    enabled,
    staleTime: STALE,
  });
}

function useActiveAlerts() {
  const tenantId = useAuthStore((s) => s.tenantId);
  return useQuery({
    queryKey: ['sm_active_alerts', tenantId],
    queryFn: () => getActiveAlertsBreakdown(tenantId!, { recentLimit: 5 }),
    enabled: !!tenantId,
    staleTime: STALE,
  });
}

function useSSEByProject(projectIds: string[], enabled: boolean) {
  return useQuery({
    queryKey: ['sm_sse_by_project', projectIds],
    queryFn: () => getSSEByProject(projectIds),
    enabled,
    staleTime: STALE,
  });
}

function useHighRiskActivity(projectIds: string[], enabled: boolean, period: TimePeriod) {
  return useQuery({
    queryKey: ['sm_high_risk', projectIds, period.days],
    queryFn: () => getHighRiskActivitySummary(projectIds, period.days),
    enabled,
    staleTime: STALE,
  });
}

function usePermitCompliance(projectIds: string[], enabled: boolean, period: TimePeriod) {
  return useQuery({
    queryKey: ['sm_permit_compliance', projectIds, period.days],
    queryFn: () => getPermitComplianceSummary(projectIds, period.days),
    enabled,
    staleTime: STALE,
  });
}

function useProjectBreakdown() {
  const tenantId = useAuthStore((s) => s.tenantId);
  return useQuery({
    queryKey: ['sm_project_breakdown', tenantId],
    queryFn: () => getProjectBreakdown(tenantId!),
    enabled: !!tenantId,
    staleTime: STALE,
  });
}

function useRecentSafetyEvents() {
  const tenantId = useAuthStore((s) => s.tenantId);
  return useQuery({
    queryKey: ['sm_recent_safety_events', tenantId],
    queryFn: () => getRecentSafetyEvents(tenantId!, 10),
    enabled: !!tenantId,
    staleTime: STALE,
  });
}

function useRecentGoodCatch() {
  const tenantId = useAuthStore((s) => s.tenantId);
  return useQuery({
    queryKey: ['sm_recent_good_catch', tenantId],
    queryFn: () => getRecentGoodCatch(tenantId!, 10),
    enabled: !!tenantId,
    staleTime: STALE,
  });
}

function useTrendAnalytics(projectIds: string[], enabled: boolean) {
  const dsrTrend = useQuery({
    queryKey: ['sm_dsr_trend', projectIds],
    queryFn: () => getDSRTrend(projectIds, 30),
    enabled,
    staleTime: STALE,
  });
  const safetyTrend = useQuery({
    queryKey: ['sm_safety_daily', projectIds],
    queryFn: () => getSafetyEventDailyTrend(projectIds, 30),
    enabled,
    staleTime: STALE,
  });
  const sseTrend = useQuery({
    queryKey: ['sm_sse_trend', projectIds],
    queryFn: () => getSSETrend(projectIds, 30),
    enabled,
    staleTime: STALE,
  });
  const actions = useQuery({
    queryKey: ['sm_top_actions', projectIds],
    queryFn: () => getTopActionItems(projectIds, 5),
    enabled,
    staleTime: STALE,
  });
  return { dsrTrend, safetyTrend, sseTrend, actions };
}

// ── Display helpers ─────────────────────────────────────────

function trendIndicator(trend: 'up' | 'down' | 'flat'): { symbol: string; color: string } {
  switch (trend) {
    case 'up':
      return { symbol: '↑', color: 'text-mozaic-red' };
    case 'down':
      return { symbol: '↓', color: 'text-mozaic-green' };
    case 'flat':
      return { symbol: '→', color: 'text-muted-foreground' };
  }
}

function formatCategory(raw: string): string {
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function safetyEventCountColor(count: number): string {
  if (count === 0) return 'text-mozaic-green';
  if (count <= 3) return 'text-mozaic-orange';
  return 'text-mozaic-red';
}

function swaEventCountColor(count: number): string {
  if (count === 0) return 'text-mozaic-green';
  if (count <= 2) return 'text-mozaic-orange';
  return 'text-mozaic-red';
}

const STATUS_BADGE: Record<string, { className: string; label: string }> = {
  draft: { className: 'bg-mozaic-orange/15 text-mozaic-orange', label: 'Draft' },
  submitted: { className: 'bg-mozaic-green/15 text-mozaic-green', label: 'Submitted' },
  reviewed: { className: 'bg-mozaic-teal/15 text-mozaic-teal', label: 'Reviewed' },
  flagged: { className: 'bg-mozaic-orange/15 text-mozaic-orange', label: 'Flagged' },
  amended: { className: 'bg-mozaic-blue/15 text-mozaic-blue', label: 'Amended' },
};

const RISK_LEVEL_STYLE: Record<string, string> = {
  critical: 'bg-mozaic-red/15 text-mozaic-red',
  high: 'bg-mozaic-red/15 text-mozaic-red',
  medium: 'bg-mozaic-orange/15 text-mozaic-orange',
  low: 'bg-mozaic-green/15 text-mozaic-green',
};

function formatRiskLevel(v: string | null): string {
  if (!v) return '—';
  return v.charAt(0).toUpperCase() + v.slice(1);
}

const ALERT_SEVERITY_ORDER: AlertSeverity[] = ['critical', 'high', 'medium', 'low'];
const ALERT_SEVERITY_STYLE: Record<AlertSeverity, string> = {
  critical: 'bg-mozaic-red/15 text-mozaic-red',
  high: 'bg-mozaic-red/15 text-mozaic-red',
  medium: 'bg-mozaic-orange/15 text-mozaic-orange',
  low: 'bg-muted text-foreground',
};

function formatEventType(type: string): string {
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Row 1: Portfolio Overview ───────────────────────────────

function PortfolioOverviewCard({
  query,
}: {
  query: ReturnType<typeof useOperationsSnapshot>;
}) {
  return (
    <Card>
      <WidgetCardHeader
        title="Portfolio Overview"
        lastUpdated={query.dataUpdatedAt}
      />
      <CardContent>
        {query.isLoading ? (
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-1">
                <Skeleton className="h-7 w-12" />
                <Skeleton className="h-3 w-24" />
              </div>
            ))}
          </div>
        ) : query.isError || !query.data ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <StatCard label="Active Projects" value={query.data.activeProjects} />
            <StatCard label="DSRs Submitted Today" value={query.data.dsrsSubmittedToday} />
            <StatCard label="SSRs on Assignment" value={query.data.ssrsOnAssignment} />
            <StatCard label="DSRs Last 30 Days" value={query.data.submittedDsrsLast30d} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Row 1: Risk Distribution L30D ───────────────────────────

function RiskDistributionCard({
  query,
  period,
  onPeriodChange,
  onRiskClick,
}: {
  query: ReturnType<typeof usePortfolioRisk>;
  period: TimePeriod;
  onPeriodChange: (p: TimePeriod) => void;
  onRiskClick: (level: 'high' | 'medium' | 'low' | 'not_assessed') => void;
}) {
  return (
    <Card>
      <WidgetCardHeader
        title="Risk Distribution"
        period={period}
        onPeriodChange={onPeriodChange}
        lastUpdated={query.dataUpdatedAt}
      />
      <CardContent>
        {query.isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : query.isError || !query.data ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : query.data.total === 0 ? (
          <p className="pt-2 text-xs text-muted-foreground">
            No submitted DSRs in the selected period
          </p>
        ) : (
          <RiskDistributionBars data={query.data} onRiskClick={onRiskClick} />
        )}
      </CardContent>
    </Card>
  );
}

function RiskDistributionBars({
  data,
  onRiskClick,
}: {
  data: RiskDistribution;
  onRiskClick: (level: 'high' | 'medium' | 'low' | 'not_assessed') => void;
}) {
  return (
    <div className="space-y-2.5">
      <button
        type="button"
        onClick={() => onRiskClick('high')}
        className="block w-full text-left hover:opacity-80"
      >
        <RiskBar label="High" pct={data.highPct} count={data.high} barClass="bg-mozaic-red" />
      </button>
      <button
        type="button"
        onClick={() => onRiskClick('medium')}
        className="block w-full text-left hover:opacity-80"
      >
        <RiskBar label="Medium" pct={data.mediumPct} count={data.medium} barClass="bg-mozaic-orange" />
      </button>
      <button
        type="button"
        onClick={() => onRiskClick('low')}
        className="block w-full text-left hover:opacity-80"
      >
        <RiskBar label="Low" pct={data.lowPct} count={data.low} barClass="bg-mozaic-green" />
      </button>
      {data.notAssessed > 0 && (
        <button
          type="button"
          onClick={() => onRiskClick('not_assessed')}
          className="block w-full text-left hover:opacity-80"
        >
          <RiskBar
            label="Not Assessed"
            pct={data.notAssessedPct}
            count={data.notAssessed}
            barClass="bg-muted-foreground/50"
          />
        </button>
      )}
      <p className="pt-1 text-xs text-muted-foreground">
        {data.total} DSR {data.total === 1 ? 'day' : 'days'} assessed
      </p>
    </div>
  );
}

// ── Row 1: Safety Events L30D ───────────────────────────────

function SafetyEventsCard({
  query,
  period,
  onPeriodChange,
  onTotalClick,
}: {
  query: ReturnType<typeof useSafetyEvents>;
  period: TimePeriod;
  onPeriodChange: (p: TimePeriod) => void;
  onTotalClick: () => void;
}) {
  return (
    <Card>
      <WidgetCardHeader
        title="Safety Events"
        period={period}
        onPeriodChange={onPeriodChange}
        lastUpdated={query.dataUpdatedAt}
      />
      <CardContent>
        {query.isLoading ? (
          <MetricSkeleton />
        ) : query.isError || !query.data ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : (
          <SafetyEventsContent data={query.data} period={period} onTotalClick={onTotalClick} />
        )}
      </CardContent>
    </Card>
  );
}

// LTI is used as the fatality proxy here per S77 Gap 4 — the
// safety_event_type ENUM does not include 'fatality', so LTI is the
// closest call-out until the enum/schema is extended.
function SafetyEventsContent({
  data,
  period,
  onTotalClick,
}: {
  data: SafetyEventsAnalysis;
  period: TimePeriod;
  onTotalClick: () => void;
}) {
  const trend = trendIndicator(data.trend);
  const lti = data.current.lost_time_injury;
  return (
    <>
      <div className="flex items-baseline gap-2">
        <p className={`text-3xl font-bold ${lti > 0 ? 'text-mozaic-red' : 'text-foreground'}`}>
          {lti}
        </p>
        <span className="text-sm text-muted-foreground">LTI</span>
        <span className={`ml-auto text-lg font-semibold ${trend.color}`}>{trend.symbol}</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Near Miss</span>
          <span className="font-medium">{data.current.near_miss}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">First Aid</span>
          <span className="font-medium">{data.current.first_aid}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Recordable</span>
          <span className="font-medium">{data.current.recordable_injury}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Property / Env.</span>
          <span className="font-medium">
            {data.current.property_damage + data.current.environmental_release}
          </span>
        </div>
      </div>
      {data.highestSeverity && (
        <p className="mt-2 text-xs">
          <span className="text-muted-foreground">Highest severity: </span>
          <span className="font-medium capitalize">{data.highestSeverity}</span>
        </p>
      )}
      {data.projectsWithLTI.length > 0 && (
        <div className="mt-2">
          <p className="text-xs font-medium text-mozaic-red">Projects with LTI:</p>
          <ul className="mt-1 space-y-0.5 text-xs">
            {data.projectsWithLTI.map((p) => (
              <li key={p.projectId} className="truncate">
                {p.projectName}
                {p.count > 1 && (
                  <span className="ml-1 text-muted-foreground">({p.count})</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      <button
        type="button"
        onClick={onTotalClick}
        className="mt-2 text-left text-xs text-muted-foreground hover:text-mozaic-teal hover:underline"
      >
        {data.current.total} total · vs {data.previous.total} in prior {period.days} days
      </button>
    </>
  );
}

// ── Row 1: Active Alerts ────────────────────────────────────

function ActiveAlertsCard({
  query,
  projectNames,
  onTotalClick,
  onSeverityClick,
}: {
  query: ReturnType<typeof useActiveAlerts>;
  projectNames: Map<string, string>;
  onTotalClick: () => void;
  onSeverityClick: (sev: AlertSeverity) => void;
}) {
  return (
    <Card>
      <WidgetCardHeader
        title="Active Alerts"
        lastUpdated={query.dataUpdatedAt}
      />
      <CardContent>
        {query.isLoading ? (
          <MetricSkeleton />
        ) : query.isError || !query.data ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : query.data.total === 0 ? (
          <>
            <p className="text-3xl font-bold text-mozaic-green">0</p>
            <p className="mt-1 text-xs text-muted-foreground">
              No unacknowledged alerts
            </p>
          </>
        ) : (
          <ActiveAlertsContent
            data={query.data}
            projectNames={projectNames}
            onTotalClick={onTotalClick}
            onSeverityClick={onSeverityClick}
          />
        )}
      </CardContent>
    </Card>
  );
}

function ActiveAlertsContent({
  data,
  projectNames,
  onTotalClick,
  onSeverityClick,
}: {
  data: ActiveAlertsBreakdown;
  projectNames: Map<string, string>;
  onTotalClick: () => void;
  onSeverityClick: (sev: AlertSeverity) => void;
}) {
  const critHigh = data.bySeverity.critical + data.bySeverity.high;
  return (
    <>
      <div className="flex items-baseline gap-2">
        <button
          type="button"
          onClick={onTotalClick}
          className={`text-3xl font-bold hover:underline ${critHigh > 0 ? 'text-mozaic-red' : 'text-foreground'}`}
        >
          {data.total}
        </button>
        <span className="text-sm text-muted-foreground">unack</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {ALERT_SEVERITY_ORDER.map((sev) => {
          const count = data.bySeverity[sev];
          if (count === 0) return null;
          return (
            <button
              key={sev}
              type="button"
              onClick={() => onSeverityClick(sev)}
              className="hover:opacity-80"
            >
              <SeverityChip className={ALERT_SEVERITY_STYLE[sev]}>
                {formatCategory(sev)} {count}
              </SeverityChip>
            </button>
          );
        })}
      </div>
      {data.projectsAtRisk.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-medium text-mozaic-red">
            Critical / High at:
          </p>
          <ul className="mt-1 space-y-0.5 text-xs">
            {data.projectsAtRisk.slice(0, 5).map((p) => (
              <li key={p.projectId} className="truncate">
                {projectNames.get(p.projectId) ?? p.projectId}
                {p.count > 1 && (
                  <span className="ml-1 text-muted-foreground">({p.count})</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

// ── Row 2: SSE Distribution table ───────────────────────────

function SSEDistributionCard({
  query,
}: {
  query: ReturnType<typeof useSSEByProject>;
}) {
  const rows = (query.data ?? []) as SSEByProjectRow[];
  const sorted = [...rows].sort((a, b) => b.ssePercent - a.ssePercent);

  return (
    <Card>
      <WidgetCardHeader
        title="SSE Distribution Across Projects"
        size="normal"
        lastUpdated={query.dataUpdatedAt}
      />
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Project</TableHead>
              <TableHead className="text-right">SSE %</TableHead>
              <TableHead className="text-right">SSE / POB</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading &&
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 4 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))}
            {query.isError && (
              <TableRow>
                <TableCell colSpan={4} className="py-6 text-center text-sm text-muted-foreground">
                  Unable to load SSE data
                </TableCell>
              </TableRow>
            )}
            {!query.isLoading && !query.isError && sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="py-6 text-center text-sm text-muted-foreground">
                  No active projects
                </TableCell>
              </TableRow>
            )}
            {sorted.map((row) => {
              const noManifest = !row.hasPublishedManifest;
              const high = row.ssePercent > 15;
              return (
                <TableRow
                  key={row.projectId}
                  className={high ? 'bg-mozaic-orange/5' : ''}
                >
                  <TableCell className="font-medium">{row.projectName}</TableCell>
                  <TableCell className="text-right">
                    {noManifest ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <span className={high ? 'font-semibold text-mozaic-orange' : ''}>
                        {row.ssePercent}%
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {noManifest ? '—' : `${row.sseCount} / ${row.totalPOB}`}
                  </TableCell>
                  <TableCell>
                    {noManifest ? (
                      <Badge variant="outline" className="text-muted-foreground">
                        No Manifest
                      </Badge>
                    ) : high ? (
                      <Badge className="bg-mozaic-orange/15 text-mozaic-orange">
                        Above 15%
                      </Badge>
                    ) : row.totalPOB === 0 ? (
                      <Badge variant="outline" className="text-muted-foreground">
                        Empty
                      </Badge>
                    ) : (
                      <Badge className="bg-mozaic-green/15 text-mozaic-green">OK</Badge>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ── Row 3: High-Risk Activity Summary ───────────────────────

function HighRiskActivityCard({
  query,
  period,
  onPeriodChange,
}: {
  query: ReturnType<typeof useHighRiskActivity>;
  period: TimePeriod;
  onPeriodChange: (p: TimePeriod) => void;
}) {
  return (
    <Card>
      <WidgetCardHeader
        title="High-Risk Activity"
        size="normal"
        period={period}
        onPeriodChange={onPeriodChange}
        lastUpdated={query.dataUpdatedAt}
      />
      <CardContent>
        {query.isLoading ? (
          <MetricSkeleton />
        ) : query.isError || !query.data ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : query.data.highCount === 0 && query.data.topRiskTags.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No high-risk activities in the selected period
          </p>
        ) : (
          <HighRiskActivityContent data={query.data} />
        )}
      </CardContent>
    </Card>
  );
}

function HighRiskActivityContent({ data }: { data: HighRiskActivitySummary }) {
  return (
    <>
      <div className="flex items-baseline gap-3">
        <p className={`text-3xl font-bold ${data.highCount > 0 ? 'text-mozaic-orange' : 'text-foreground'}`}>
          {data.highCount}
        </p>
        <span className="text-xs text-muted-foreground">
          high/critical activities
        </span>
      </div>
      {data.daysWithSimopsCount > 0 && (
        <p className="mt-1 text-xs">
          <span className="text-muted-foreground">SIMOPS detected on </span>
          <span className="font-medium">{data.daysWithSimopsCount}</span>
          <span className="text-muted-foreground">
            {' '}day{data.daysWithSimopsCount === 1 ? '' : 's'}
          </span>
        </p>
      )}
      {data.topRiskTags.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-medium text-muted-foreground">Top Risk Tags</p>
          <ul className="mt-1 space-y-0.5 text-xs">
            {data.topRiskTags.map((t) => (
              <li key={t.tag} className="flex justify-between">
                <span className="truncate">{formatCategory(t.tag)}</span>
                <span className="font-medium">{t.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {data.projectsByHighCount.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-medium text-muted-foreground">
            Highest concentration
          </p>
          <ul className="mt-1 space-y-0.5 text-xs">
            {data.projectsByHighCount.slice(0, 3).map((p) => (
              <li key={p.projectId} className="flex justify-between">
                <span className="truncate">{p.projectName}</span>
                <span className="font-medium">{p.highCount}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

// ── Row 3: Permit Compliance Summary ────────────────────────

function PermitComplianceCard({
  query,
  period,
  onPeriodChange,
}: {
  query: ReturnType<typeof usePermitCompliance>;
  period: TimePeriod;
  onPeriodChange: (p: TimePeriod) => void;
}) {
  return (
    <Card>
      <WidgetCardHeader
        title="Permit Compliance"
        size="normal"
        period={period}
        onPeriodChange={onPeriodChange}
        lastUpdated={query.dataUpdatedAt}
      />
      <CardContent>
        {query.isLoading ? (
          <MetricSkeleton />
        ) : query.isError || !query.data ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : query.data.total === 0 ? (
          <p className="text-xs text-muted-foreground">No permits in the selected period</p>
        ) : (
          <PermitComplianceContent data={query.data} />
        )}
      </CardContent>
    </Card>
  );
}

function PermitComplianceContent({ data }: { data: PermitComplianceSummary }) {
  const notVerified = data.total - data.verifiedCount;
  const verifiedPct =
    data.total > 0 ? Math.round((data.verifiedCount / data.total) * 100) : 0;
  const byTypeSorted = Object.entries(data.byType).sort((a, b) => b[1] - a[1]);
  return (
    <>
      <div className="flex items-baseline gap-3">
        <p className="text-3xl font-bold">{data.total}</p>
        <span className="text-xs text-muted-foreground">permits opened</span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <div>
          <p className="text-muted-foreground">Verified</p>
          <p className="font-semibold text-mozaic-green">
            {data.verifiedCount}
            <span className="ml-1 text-xs font-normal text-muted-foreground">
              ({verifiedPct}%)
            </span>
          </p>
        </div>
        <div>
          <p className="text-muted-foreground">Not Verified</p>
          <p className={`font-semibold ${notVerified > 0 ? 'text-mozaic-orange' : ''}`}>
            {notVerified}
          </p>
        </div>
        <div>
          <p className="text-muted-foreground">Expired</p>
          <p className={`font-semibold ${data.expiredCount > 0 ? 'text-mozaic-red' : ''}`}>
            {data.expiredCount}
          </p>
        </div>
      </div>
      {byTypeSorted.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-medium text-muted-foreground">By Type</p>
          <ul className="mt-1 space-y-0.5 text-xs">
            {byTypeSorted.map(([type, count]) => (
              <li key={type} className="flex justify-between">
                <span className="truncate capitalize">{formatCategory(type)}</span>
                <span className="font-medium">{count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

// ── Project Breakdown table (with S80 extended columns) ─────

type SortKey = keyof Pick<
  ProjectBreakdownRow,
  | 'projectName'
  | 'businessUnitName'
  | 'divisionName'
  | 'currentLocation'
  | 'dsrsTodayCount'
  | 'workforce'
  | 'safetyEvents30d'
  | 'swaEvents30d'
  | 'lastSubmitted'
  | 'lastRiskLevel'
>;

function sortProjects(
  rows: ProjectBreakdownRow[],
  key: SortKey,
  asc: boolean,
): ProjectBreakdownRow[] {
  return [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp =
      typeof av === 'string'
        ? av.localeCompare(bv as string)
        : (av as number) - (bv as number);
    return asc ? cmp : -cmp;
  });
}

function SortableHead({
  label, sortKey, currentKey, asc, onSort, align,
}: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  asc: boolean;
  onSort: (key: SortKey) => void;
  align?: 'right';
}) {
  const isActive = currentKey === sortKey;
  return (
    <TableHead
      className={`cursor-pointer select-none hover:text-foreground ${align === 'right' ? 'text-right' : ''}`}
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive && <span className="ml-1">{asc ? '↑' : '↓'}</span>}
    </TableHead>
  );
}

// ── Main ────────────────────────────────────────────────────

export default function SafetyManagerDashboard() {
  const navigate = useNavigate();
  const { dsrId: panelDsrId, openDsr, closeDsr } = useDsrPanel();
  const tenantName = useTenantName();
  const scope = useScopeProjectIds();
  const projectIds = React.useMemo(() => scope.data ?? [], [scope.data]);
  const scopeReady = !!scope.data;

  // Per-widget time-period state (AC 12.1.10) — Safety Manager defaults are L30D.
  const [riskPeriod, setRiskPeriod] = React.useState<TimePeriod>(TIME_PERIOD_30D);
  const [safetyPeriod, setSafetyPeriod] = React.useState<TimePeriod>(TIME_PERIOD_30D);
  const [highRiskPeriod, setHighRiskPeriod] = React.useState<TimePeriod>(TIME_PERIOD_30D);
  const [permitPeriod, setPermitPeriod] = React.useState<TimePeriod>(TIME_PERIOD_30D);

  const opsSnapshot = useOperationsSnapshot();
  const portfolioRisk = usePortfolioRisk(projectIds, scopeReady, riskPeriod);
  const safetyEvents = useSafetyEvents(projectIds, scopeReady, safetyPeriod);
  const activeAlerts = useActiveAlerts();
  const sseByProject = useSSEByProject(projectIds, scopeReady);
  const highRisk = useHighRiskActivity(projectIds, scopeReady, highRiskPeriod);
  const permits = usePermitCompliance(projectIds, scopeReady, permitPeriod);
  const projectBreakdown = useProjectBreakdown();
  const recentSafety = useRecentSafetyEvents();
  const recentGoodCatch = useRecentGoodCatch();
  const trends = useTrendAnalytics(projectIds, scopeReady);

  const projectNames = React.useMemo(() => {
    const m = new Map<string, string>();
    (projectBreakdown.data ?? []).forEach((p) => m.set(p.projectId, p.projectName));
    return m;
  }, [projectBreakdown.data]);

  const dsrTrendState = useChartState(trends.dsrTrend, 'No submitted DSRs in this period');
  const dsrTrendEmpty =
    !trends.dsrTrend.isLoading &&
    !trends.dsrTrend.isError &&
    (trends.dsrTrend.data ?? []).every((p) => p.dsr_count === 0);
  const safetyTrendState = useChartState(trends.safetyTrend, 'No safety events recorded');
  const safetyTrendEmpty =
    !trends.safetyTrend.isLoading &&
    !trends.safetyTrend.isError &&
    (trends.safetyTrend.data ?? []).every(
      (p) =>
        p.near_miss_count +
          p.first_aid_count +
          p.recordable_count +
          p.lti_count +
          p.good_catch_count ===
        0,
    );
  const sseTrendHasData = (trends.sseTrend.data ?? []).length > 0;

  const [sortKey, setSortKey] = React.useState<SortKey>('projectName');
  const [sortAsc, setSortAsc] = React.useState(true);
  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const sortedProjects = projectBreakdown.data
    ? sortProjects(projectBreakdown.data, sortKey, sortAsc)
    : [];

  const PROJECT_COLSPAN = 11;

  return (
    <div className="space-y-6">
      {/* Scope context bar */}
      <div className="rounded-md bg-mozaic-navy/5 px-4 py-2 text-sm">
        Viewing:{' '}
        <span className="font-medium">{tenantName.data ?? 'Your Organization'}</span>{' '}
        — All Operations
      </div>

      {/* Row 1: Tenant-Wide Status */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <PortfolioOverviewCard query={opsSnapshot} />
        <RiskDistributionCard
          query={portfolioRisk}
          period={riskPeriod}
          onPeriodChange={setRiskPeriod}
          onRiskClick={(level) =>
            navigate(level === 'not_assessed' ? '/reports' : `/reports?risk=${level}`)
          }
        />
        <SafetyEventsCard
          query={safetyEvents}
          period={safetyPeriod}
          onPeriodChange={setSafetyPeriod}
          onTotalClick={() => navigate('/reports')}
        />
        <ActiveAlertsCard
          query={activeAlerts}
          projectNames={projectNames}
          onTotalClick={() => navigate('/alerts')}
          onSeverityClick={(sev) => navigate(`/alerts?severity=${sev}`)}
        />
      </div>

      {/* Row 2: Workforce Intelligence */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SSEDistributionCard query={sseByProject} />
        <ChartCard
          title="Workforce SSE % — Last 30 Days"
          isLoading={trends.sseTrend.isLoading}
          isError={trends.sseTrend.isError}
          isEmpty={!sseTrendHasData && !trends.sseTrend.isLoading}
          emptyMessage="No POB manifests in this period"
          onRetry={trends.sseTrend.refetch}
        >
          <SSEAreaChart data={trends.sseTrend.data ?? []} />
        </ChartCard>
      </div>

      {/* Row 3: Activity and Permit Intelligence */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <HighRiskActivityCard
          query={highRisk}
          period={highRiskPeriod}
          onPeriodChange={setHighRiskPeriod}
        />
        <PermitComplianceCard
          query={permits}
          period={permitPeriod}
          onPeriodChange={setPermitPeriod}
        />
      </div>

      {/* Preserved: Daily Risk + Safety Event trend charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard
          title="Daily Risk Level — Last 30 Days"
          {...dsrTrendState}
          isEmpty={dsrTrendEmpty}
        >
          <DailyRiskChart data={trends.dsrTrend.data ?? []} />
        </ChartCard>
        <ChartCard
          title="Safety Events — Last 30 Days"
          {...safetyTrendState}
          isEmpty={safetyTrendEmpty}
        >
          <SafetyEventLineChart data={trends.safetyTrend.data ?? []} />
        </ChartCard>
      </div>

      {/* Preserved: Top Action Items */}
      <TopActionItemsCard
        items={trends.actions.data}
        isLoading={trends.actions.isLoading}
        isError={trends.actions.isError}
        onViewAll={() => navigate('/reports')}
      />

      {/* Project Breakdown (extended with BU/Division/Location/Last Risk) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Project Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead label="Project" sortKey="projectName" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                <SortableHead label="Business Unit" sortKey="businessUnitName" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                <SortableHead label="Division" sortKey="divisionName" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                <SortableHead label="Location" sortKey="currentLocation" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                <SortableHead label="DSRs Today" sortKey="dsrsTodayCount" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                <TableHead>Status</TableHead>
                <SortableHead label="Last Risk" sortKey="lastRiskLevel" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                <SortableHead label="Workforce" sortKey="workforce" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                <SortableHead label="Safety (30d)" sortKey="safetyEvents30d" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                <SortableHead label="SWA (30d)" sortKey="swaEvents30d" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                <SortableHead label="Last Submitted" sortKey="lastSubmitted" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {projectBreakdown.isLoading &&
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: PROJECT_COLSPAN }).map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))}
              {projectBreakdown.isError && (
                <TableRow>
                  <TableCell colSpan={PROJECT_COLSPAN} className="py-8 text-center text-sm text-muted-foreground">
                    Could not load project data.
                  </TableCell>
                </TableRow>
              )}
              {!projectBreakdown.isLoading &&
                !projectBreakdown.isError &&
                sortedProjects.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={PROJECT_COLSPAN} className="py-10 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <FolderOpen className="h-7 w-7 text-muted-foreground/60" />
                        <p className="text-sm font-medium">No active projects yet.</p>
                        <p className="text-xs">
                          Projects appear once they are created and linked to a location.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              {sortedProjects.map((row) => {
                const statusBadge = row.latestStatus
                  ? STATUS_BADGE[row.latestStatus] ?? STATUS_BADGE.draft
                  : null;
                return (
                  <TableRow
                    key={row.projectId}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/reports?project=${row.projectId}`)}
                  >
                    <TableCell className="font-medium">{row.projectName}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.businessUnitName ?? '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.divisionName ?? '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.currentLocation ?? '—'}
                    </TableCell>
                    <TableCell>{row.dsrsTodayCount}</TableCell>
                    <TableCell>
                      {statusBadge ? (
                        <Badge className={statusBadge.className}>{statusBadge.label}</Badge>
                      ) : (
                        <span className="text-muted-foreground">No DSR</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.lastRiskLevel ? (
                        <Badge className={`capitalize ${RISK_LEVEL_STYLE[row.lastRiskLevel] ?? ''}`}>
                          {formatRiskLevel(row.lastRiskLevel)}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>{row.workforce}</TableCell>
                    <TableCell>
                      <span className={safetyEventCountColor(row.safetyEvents30d)}>
                        {row.safetyEvents30d}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={swaEventCountColor(row.swaEvents30d)}>
                        {row.swaEvents30d}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.lastSubmitted
                        ? formatDistanceToNow(new Date(row.lastSubmitted), { addSuffix: true })
                        : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Preserved audit feeds — shared-component extraction deferred to S82 */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm">Recent Safety Events</CardTitle>
          <Button variant="link" size="sm" className="text-mozaic-teal" onClick={() => navigate('/reports')}>
            View All Reports &rarr;
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Event Type</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Submitted By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentSafety.isLoading &&
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))}
              {!recentSafety.isLoading &&
                !recentSafety.isError &&
                (recentSafety.data?.length ?? 0) === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Inbox className="h-7 w-7 text-muted-foreground/60" />
                        <p className="text-sm font-medium">No safety events in the last 30 days.</p>
                        <p className="text-xs">A clean slate — keep it up.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              {recentSafety.data?.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => openDsr(row.dsrReportId)}
                >
                  <TableCell className="text-xs">
                    {format(new Date(row.eventDatetime), 'MMM d, yyyy HH:mm')}
                  </TableCell>
                  <TableCell>{row.projectName}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{formatEventType(row.eventType)}</Badge>
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate">{row.eventTitle}</TableCell>
                  <TableCell className="text-muted-foreground">{row.createdByName}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm">Recent Good Catch Observations</CardTitle>
          <Button variant="link" size="sm" className="text-mozaic-teal" onClick={() => navigate('/reports')}>
            View All Reports &rarr;
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentGoodCatch.isLoading &&
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 4 }).map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))}
              {!recentGoodCatch.isLoading &&
                !recentGoodCatch.isError &&
                (recentGoodCatch.data?.length ?? 0) === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="py-10 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Inbox className="h-7 w-7 text-muted-foreground/60" />
                        <p className="text-sm font-medium">No Good Catch observations yet.</p>
                        <p className="text-xs">
                          Encourage SSRs to log positive observations on each shift.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              {recentGoodCatch.data?.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => openDsr(row.dsrReportId)}
                >
                  <TableCell className="text-xs">
                    {format(new Date(row.createdAt), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>{row.projectName}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {row.observationCategory.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-[300px] truncate">{row.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <DSRDetailPanel dsrId={panelDsrId} onClose={closeDsr} />
    </div>
  );
}
